import type { CalendarOperation } from "../types";

// Extended error type for detailed error information from server
interface ExtendedError extends Error {
  type?: string;
  stage?: string;
  details?: {
    stage?: string;
    timestamp?: string;
  };
}

// Progress update interface for streaming
export interface ProgressUpdate {
  stage: string;
  percent: number;
  message: string;
}

/**
 * Performs a background warmup request to cache the schema on Claude's side
 * This is a fire-and-forget operation that doesn't return results or affect the UI
 * @param accessToken - Google access token for authentication
 * @param userProfile - User profile information
 */
export const warmupSchemaCache = async (
  accessToken: string,
  userProfile: { name: string; email: string }
): Promise<void> => {
  try {
    // Get user's timezone from browser
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Make a minimal warmup request in the background
    // Use a simple input that won't result in any operations
    fetch("/api/parse-events-stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        userInput: "warmup",
        userProfile,
        userTimeZone,
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          // Silently ignore errors during warmup
          console.debug("Warmup request failed, but this is non-critical");
          return;
        }

        // Consume the response stream to complete the request
        const reader = response.body?.getReader();
        if (!reader) return;

        // Read the stream to completion without processing
        while (true) {
          const { done } = await reader.read();
          if (done) break;
        }

        console.debug("Schema cache warmed up successfully");
      })
      .catch(() => {
        // Silently ignore network errors during warmup
        console.debug("Warmup request encountered an error, but this is non-critical");
      });
  } catch {
    // Silently fail - warmup is not critical
    console.debug("Schema warmup skipped due to error");
  }
};

/**
 * Calls the backend API to parse events from natural language text with streaming progress updates
 * Uses Server-Sent Events (SSE) to stream progress in real-time
 * @param userInput - The natural language input from the user
 * @param userProfile - User profile information
 * @param accessToken - Google access token for authentication
 * @param onProgress - Callback function called with progress updates
 * @returns Promise resolving to calendar operations
 */
export const parseEventsFromTextWithProgress = async (
  userInput: string,
  userProfile: { name: string; email: string },
  accessToken: string,
  onProgress: (progress: ProgressUpdate) => void,
  fileContent?: string | null,
  fileMediaType?: string | null
): Promise<CalendarOperation[]> => {
  return new Promise((resolve, reject) => {
    // Get user's timezone from browser
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Use fetch with streaming response
    fetch("/api/parse-events-stream", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        userInput,
        userProfile,
        userTimeZone,
        ...(fileContent && fileMediaType ? { fileContent, fileMediaType } : {}),
      }),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));

          // Handle 401 specifically - token is invalid or expired
          if (response.status === 401) {
            throw new Error("UNAUTHORIZED");
          }

          const errorMessage =
            errorData.message || errorData.error || `Server returned ${response.status}`;
          throw new Error(errorMessage);
        }

        // Process SSE stream
        const reader = response.body?.getReader();

        const decoder = new TextDecoder();

        if (!reader) {
          throw new Error("Response body is not readable");
        }

        let buffer = "";

        while (true) {
          const { done, value } = await reader.read();

          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages (ending with \n\n)
          const lines = buffer.split("\n\n");
          buffer = lines.pop() || ""; // Keep incomplete message in buffer

          for (const line of lines) {
            if (line.startsWith("data: ")) {
              const data = JSON.parse(line.substring(6));

              if (data.stage === "error") {
                // Error received
                const error = new Error(data.error) as ExtendedError;
                error.type = data.type;
                error.details = data.details;
                reject(error);
                return;
              } else if (data.stage === "result") {
                // Final result received
                resolve(data.operations as CalendarOperation[]);
                return;
              } else {
                // Progress update
                onProgress(data as ProgressUpdate);
              }
            }
          }
        }
      })
      .catch((e) => {
        console.error("Error parsing events with streaming API:", e);
        // Re-throw UNAUTHORIZED errors so the UI can handle them
        if (e instanceof Error && e.message === "UNAUTHORIZED") {
          reject(e);
          return;
        }
        // If it's already a detailed error, re-throw it
        if (e instanceof Error && (e as ExtendedError).type) {
          reject(e);
          return;
        }
        // Generic error for network issues
        reject(
          new Error(
            "Failed to communicate with the server. Please check your connection and try again."
          )
        );
      });
  });
};
